import { CartAddEvent, ThemeEvents } from '@theme/events'
import { useDialog } from '@theme/use-dialog'

const CartDrawerConfig = {
  autoOpenBaseDelay: 150,
  maxAnimationDelay: 900,
  openOnLoad: {
    param: 'drawer-cart',
    pathname: 'cart',
    value: 'active'
  },
  selectors: {
    cta: '#shopify-installments-cta',
    paymentTerms: 'shopify-payment-terms'
  }
}

function shouldOpenDrawerCartOnLoad() {
  try {
    const params = new URLSearchParams(window.location.search)
    if (
      params.get(CartDrawerConfig.openOnLoad.param) ===
      CartDrawerConfig.openOnLoad.value
    ) {
      return true
    }

    const root = window.Shopify?.routes?.root ?? '/'
    const cartPath = `${root}${CartDrawerConfig.openOnLoad.pathname}`.replace(
      /\/+$/,
      ''
    )
    const currentPath = window.location.pathname.replace(/\/+$/, '')
    return currentPath === cartPath
  } catch {
    return false
  }
}

function openDrawerCartOnLoad() {
  const open = () => {
    const drawer = document.querySelector('drawer-cart')
    if (drawer && typeof drawer.open === 'function') {
      drawer.open()
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', open, { once: true })
  } else {
    open()
  }
}

class CartDrawerComponent extends useDialog {
  connectedCallback() {
    super.connectedCallback()
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd)
  }

  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) {
      return
    }

    if (event?.detail?.preventAutoOpen) {
      return
    }

    if (event?.detail?.isRewardAction) {
      return
    }

    if (event?.detail?.data?.didError) {
      return
    }

    if (this.refs?.dialog?.open || this.dataset.nav === 'open') {
      return
    }

    const delay = this.#resolveAutoOpenDelay(event)

    setTimeout(() => {
      this.showDialog()
      document.dispatchEvent(new CustomEvent(ThemeEvents.cartDrawerOpen))
    }, delay)
  }

  #resolveAutoOpenDelay(event) {
    const animationDuration = Number(
      event?.detail?.data?.animationDuration ?? 0
    )
    const safeAnimationDuration = Number.isFinite(animationDuration)
      ? Math.min(
          Math.max(animationDuration, 0),
          CartDrawerConfig.maxAnimationDelay
        )
      : 0

    return safeAnimationDuration + CartDrawerConfig.autoOpenBaseDelay
  }

  open() {
    this.showDialog()
    document.dispatchEvent(new CustomEvent(ThemeEvents.cartDrawerOpen))
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector(
        CartDrawerConfig.selectors.paymentTerms
      )?.shadowRoot
      const cta = installmentsContent?.querySelector(
        CartDrawerConfig.selectors.cta
      )
      cta?.addEventListener('click', this.closeDialog, { once: true })
    })
  }
}

customElements.define('drawer-cart', CartDrawerComponent)

if (shouldOpenDrawerCartOnLoad()) {
  openDrawerCartOnLoad()
}
